import { useCallback, useEffect, useRef, useState } from "react";
import * as MessageFormat from "./MessageFormat";
import {
  Card,
  CircularProgress,
  IconButton,
  styled,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Send } from "@material-ui/icons";
import ArchiveIcon from "@material-ui/icons/Archive";
import UnarchiveIcon from "@material-ui/icons/Unarchive";
import { useDispatch, useSelector } from "react-redux";
import { markChatRead, sendText, setSmsTask, archiveSmsTask, unarchiveSmsTask } from "./actions";
import * as coreSelectors from "core/selectors";
import * as serviceSelectors from "service/selectors";
import { formatPhoneNumber, formatShortDateTime } from "ui/Formatters";
import CloseIcon from "@material-ui/icons/Close";


const ChatBox = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  maxHeight: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  padding: "10px 0",
  backgroundColor: "#fff",
  borderRadius: "15px",
  zIndex: 10,
  height:'91%',
  [theme.breakpoints.up("md")]: {
    width: "100%",
    borderRadius: "0px",
    padding: 10,
    zIndex: "auto",
    position: "relative",
    backgroundColor: "transparent",
    height:'94%',
    borderLeft: `1px dashed ${theme.palette.text.disabled}`,
  },
}));
const MessagesContainer = styled("div")(() => ({
  display: "flex",
  overflowY: "scroll",
  overflowX: "hidden",
  height: "100%",
  maxHeight: "100%",
  flexDirection: "column",
  scrollBehavior: "smooth",
  padding: "0 5px",
}));
const MessageContainer = styled("div")(({ outbound, theme }) => ({
  alignSelf: outbound ? "flex-end" : "flex-start",
  marginBottom: "10px",
  paddingTop: "10px",
  fontSize: "14px",
}));
const MessageMetaData = styled("p")(() => ({
  color: "#000",
  margin: "0",
  fontFamily: "Sofia",
  // fontWeight: 600,
  fontSize: "0.82em",
}));
const MessageBox = styled(Card)(({ outbound, theme }) => ({
  backgroundColor: outbound ? theme.brandingColors.rubyBlue : "#eee",
  color: outbound ? "#fff" : "#000",
  padding: "8px 15px",
  borderRadius: outbound ? "20px 20px 0px 20px" : "20px 20px 20px 0px",
  fontSize: "1em",
  fontFamily: "Sofia",
}));

const ToolBar = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.text.disabled}`,
  "& >*": {
    marginLeft: "5px",
  },
}));

const InputBox = styled("form")(({ theme, disabled = false }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  width: "100%",
  position: "sticky",
  bottom: 0,
  height: "50px",
  fontSize: "10px",
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline ": {
    borderColor: disabled ? theme.palette.grey[100] : theme.palette.grey[200],
  },
  "& .MuiTextField-root": {
    width: "100%",
    marginRight: "3px",
  },
  "& .MuiInputBase-root, .MuiTextField-root": {
    height: "100%",
    borderRadius: "15px",
    fontSize: "1em",
  },
  "& .MuiIconButton-root": {
    borderRadius: "15px",
    background: theme.brandingColors.rubyDarkPurple,
    color: "#fff",
  },
  "& .MuiInputBase-input": {
    fontSize: "1.2em",
    fontFamily: "Sofia",
  },
}));
const StyledIconButton = styled(IconButton)(({ theme, disabled = false }) => ({
  background: `${
    disabled ? theme.palette.grey[200] : theme.brandingColors.rubyDarkPurple
  } !important`,
}));
const StyledSendIcon = styled(Send)(() => ({
  height: "0.8em !important",
  width: "0.8em !important",
}));
const StyledCircularProgress = styled(CircularProgress)(() => ({
  height: "0.8em !important",
  width: "0.8em !important",
  color: "#fff",
}));

const Image = styled("img")(() => ({
  maxHeight: "50vh",
  minHeight: "300px",
}));
export default function TextThread({
  selectedTaskInfo,
  onChatClose = () => {},
}) {
  const dispatch = useDispatch();
  const messagesContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const user = useSelector((state) => coreSelectors.getUser(state));
  const sendingText = useSelector((state) =>
    serviceSelectors.getSendingText(state)
  );
  const [messageText, setMessageText] = useState("");
  const closeChat = () => {
    onChatClose();
    // dataTableRef.current.resetSelection();
    dispatch(setSmsTask({ messages: [] }));
  };
  const handleArchiveToggle = () => {
    const lastMessageId = selectedTaskInfo?.lastMessageId || 
      (selectedTaskInfo?.messages && selectedTaskInfo.messages.length > 0 
        ? selectedTaskInfo.messages[selectedTaskInfo.messages.length - 1].messageId 
        : null);
    
    if (!lastMessageId) return;
    if (selectedTaskInfo?.messageArchived) {
      dispatch(unarchiveSmsTask(selectedTaskInfo.taskId, lastMessageId));
    } else {
      dispatch(archiveSmsTask(selectedTaskInfo.taskId, lastMessageId));
    }
  };
  const handleSendText = (e) => {
    e.preventDefault();
    dispatch(
      sendText(
        selectedTaskInfo.phoneNumberId,
        selectedTaskInfo.ani,
        messageText,
        () => setMessageText("")
      )
    );
  };
  const canSendText = (phoneNumberId) => {
    const phoneNumber = (user?.patchNumbers || []).find(
      (number) => number.phoneNumberId === phoneNumberId
    );
    return phoneNumber?.status === "ACTIVE" && phoneNumber?.dlcVerified === "Y";
  };
  const doScroll = useCallback(() => {
    // Clear any pending scroll
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    // Set new scroll timeout
    scrollTimeoutRef.current = setTimeout(() => {
      if (messagesContainerRef.current) {
        window.requestAnimationFrame(function () {
          messagesContainerRef.current.scrollTop =
            messagesContainerRef.current.scrollHeight;
        });
      }
    }, 100);
  }, []);
  useEffect(() => {
    doScroll();
    // If this sms task is open and there is a change to the smsTasks
    if (
      selectedTaskInfo?.taskId &&
      typeof selectedTaskInfo?.unread === "number" &&
      selectedTaskInfo?.unread > 0
    ) {
      dispatch(markChatRead(selectedTaskInfo.taskId));
    }
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [selectedTaskInfo, dispatch, doScroll]);
  return (
    <ChatBox>
      <ToolBar>
        <IconButton style={{ cursor: "pointer" }} onClick={closeChat}>
          <CloseIcon />
        </IconButton>
        <Typography
          style={{
            fontSize: "14px",
            fontFamily: "Sofia",
            fontWeight: 600,
            flex: 1,
          }}
        >
          {selectedTaskInfo?.smsDisplayName
            ? selectedTaskInfo?.smsDisplayName
            : MessageFormat.messageDetail(selectedTaskInfo, user)}
        </Typography>
        <Tooltip title={selectedTaskInfo?.messageArchived ? "Unarchive" : "Archive"}>
          <IconButton 
            onClick={handleArchiveToggle}
            style={{ cursor: "pointer" }}
          >
            {selectedTaskInfo?.messageArchived ? (
              <UnarchiveIcon fontSize="medium" style={{ color: '#122457' }} />
            ) : (
              <ArchiveIcon fontSize="medium" style={{ color: '#122457' }} />
            )}
          </IconButton>
        </Tooltip>
      </ToolBar>
      <MessagesContainer ref={messagesContainerRef}>
        {selectedTaskInfo.messages.map((msg) => {
          const isOutbound = !!msg.sendingUser;
          // console.log("The message is", msg)
          return (
            <MessageContainer key={msg.messageId} outbound={isOutbound}>
              <MessageMetaData>
                {formatShortDateTime(msg?.taken)} -{" "}
                <b>{formatPhoneNumber(isOutbound ? msg.dnis : msg.ani)}</b>{" "}
                {isOutbound && (
                  <>
                    - <b>{selectedTaskInfo.accountName}</b> -{" "}
                    <b>{msg.sendingUser}</b>
                  </>
                )}
              </MessageMetaData>
              <Message
                outbound={isOutbound}
                msg={msg}
                onImageLoad={() => doScroll()}
              />
            </MessageContainer>
          );
        })}
      </MessagesContainer>
      {canSendText(selectedTaskInfo?.phoneNumberId) && (
        <InputBox disabled={sendingText} onSubmit={handleSendText}>
          <TextField
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type a message"
            disabled={sendingText}
            inputProps={{ maxLength: 1000 }}
          />
          <StyledIconButton
            type="submit"
            disabled={!messageText.length || sendingText}
          >
            {sendingText ? <StyledCircularProgress /> : <StyledSendIcon />}
          </StyledIconButton>
        </InputBox>
      )}
    </ChatBox>
  );
}

const hasImage = (msg) => {
  try {
    const form = JSON.parse(msg.formContent);
    const images = form.fields.reduce((images, currentField) => {
      if (currentField.type === "image" && currentField.value) {
        return [...images, currentField.value];
      }
      return images;
    }, []);
    return images.length ? images : null;
  } catch (e) {
    return null;
  }
};
const Message = ({ msg, outbound, onImageLoad }) => {
  const images = hasImage(msg);
  if (images) {
    return (
      <MessageBox outbound={outbound}>
        {images.map((img, index) => {
          return (
            <Image key={index} src={img} onLoad={onImageLoad} alt="image" />
          );
        })}
      </MessageBox>
    );
  }
  return <MessageBox outbound={outbound}>{msg.textContent}</MessageBox>;
};
